import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Controller } from "react-hook-form";

const SearchDropdown = ({
  isDisabled: isDisabledProp = false,
  /** Alias used by some screens (e.g. OPD Billing); merges with isDisabled */
  disabled = false,
  isMulti = false,
  isSearchable = false,
  isClearable = true,
  isFloatingLabel = true,
  control,
  name,
  dataArray = [],
  placeholder = "",
  label = "",
  onChange,
  onInputChange,
  onSearch,
  defaultValue = null,
  value: controlledValue,
  onBlur,
  fontSize = "text-sm",
  selectSize = "h-8",
  leftIcon,
  rightIcon,
  iconBgColor = "bg-gray-100",
  inputBgColor = "bg-gray-50",
  onLeftIconClick,
  onRightIconClick,
  chipColor = "bg-blue-100",
  chipTextColor = "text-blue-800",
  maxChipsDisplay = 3,
  error,
  isLoading = false,
  menuPosition = "auto",
  /** Render dropdown menu in document.body (use inside modals to avoid clipping) */
  menuPortal = false,
  openOnFocus = false,
  onEnterWhenClosed,
  onCtrlEnterWhenClosed,
  onTabThenEnterWhenClosed,
  onCreateOption,
  ...props
}) => {
  const isDisabled = Boolean(isDisabledProp || disabled);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState(
    menuPosition === "auto" ? "bottom" : menuPosition
  );
  const [portalMenuStyle, setPortalMenuStyle] = useState(null);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const tabPressedWhenClosedRef = useRef(false);
  /**
   * When openOnFocus is true, the same mouse gesture fires focus (opens) then click (toggles).
   * Without skipping one toggle, the menu opens then immediately closes — feels like "must click twice".
   */
  const suppressNextHeaderToggleRef = useRef(false);

  const hasLeftIcon = !!leftIcon;
  const hasRightIcon = !!rightIcon;

  const getHeightClass = () => {
    switch (selectSize) {
      case "h-8":
        return "h-8";
      case "h-9":
        return "h-9";
      case "h-10":
        return "h-10";
      case "h-11":
        return "h-11";
      case "h-12":
        return "h-12";
      default:
        return "h-9";
    }
  };

  const applyPortalMenuPosition = useCallback(
    (placement) => {
      if (!menuPortal || !wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const gap = 4;
      const maxHeight = 240;

      if (placement === "top") {
        setPortalMenuStyle({
          position: "fixed",
          left: rect.left,
          width: rect.width,
          bottom: window.innerHeight - rect.top + gap,
          maxHeight,
          zIndex: 99999,
        });
      } else {
        setPortalMenuStyle({
          position: "fixed",
          top: rect.bottom + gap,
          left: rect.left,
          width: rect.width,
          maxHeight,
          zIndex: 99999,
        });
      }
    },
    [menuPortal]
  );

  const calculateDropdownPosition = useCallback(() => {
    let placement = menuPosition !== "auto" ? menuPosition : "bottom";

    if (menuPosition === "auto") {
      if (!wrapperRef.current || !isOpen) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 240;
      const spacing = 8;

      const spaceBelow = viewportHeight - rect.bottom - spacing;
      const spaceAbove = rect.top - spacing;

      const shouldOpenBelow =
        spaceBelow >= dropdownMaxHeight || spaceBelow >= spaceAbove;

      placement = shouldOpenBelow ? "bottom" : "top";
    }

    setDropdownPosition(placement);
    if (menuPortal) applyPortalMenuPosition(placement);
  }, [isOpen, menuPosition, menuPortal, applyPortalMenuPosition]);

  const updatePortalMenuPosition = useCallback(() => {
    applyPortalMenuPosition(dropdownPosition);
  }, [applyPortalMenuPosition, dropdownPosition]);

  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen, calculateDropdownPosition]);

  useEffect(() => {
    if (isOpen && menuPortal) {
      updatePortalMenuPosition();
    } else if (!isOpen) {
      setPortalMenuStyle(null);
    }
  }, [isOpen, menuPortal, dropdownPosition, updatePortalMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculateDropdownPosition();
      if (menuPortal) updatePortalMenuPosition();
    };

    const handleScroll = () => {
      calculateDropdownPosition();
      if (menuPortal) updatePortalMenuPosition();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, calculateDropdownPosition, menuPortal, updatePortalMenuPosition]);

  const findOptionInDataArray = useCallback((raw) => {
    if (raw === null || raw === undefined || raw === "") return null;
    const direct = dataArray.find(
      (opt) => opt.value === raw || opt.id === raw
    );
    if (direct) return direct;
    const s = String(raw);
    return (
      dataArray.find(
        (opt) => String(opt.value) === s || String(opt.id ?? "") === s
      ) || null
    );
  }, [dataArray]);

  const normalizeValue = useCallback(
    (value) => {
      if (value === null || value === undefined) return isMulti ? [] : null;

      if (isMulti) {
        const values = Array.isArray(value) ? value : [value];
        return values
          .map((v) => {
            if (
              typeof v === "object" &&
              v !== null &&
              v.label !== undefined &&
              v.value !== undefined
            )
              return v;
            return findOptionInDataArray(v);
          })
          .filter(Boolean);
      }

      if (
        typeof value === "object" &&
        value !== null &&
        value.label !== undefined &&
        value.value !== undefined
      )
        return value;

      return findOptionInDataArray(value);
    },
    [findOptionInDataArray, isMulti]
  );

  useEffect(() => {
    if (!isSearchable || !searchTerm || !onSearch) return;

    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      Promise.resolve(onSearch(searchTerm)).finally(() =>
        setIsSearching(false)
      );
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isSearchable, onSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inWrapper = wrapperRef.current?.contains(event.target);
      const inMenu = listRef.current?.contains(event.target);
      if (!inWrapper && !inMenu) {
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        if (onBlur) onBlur(event);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onBlur]);

  const filteredOptions = useMemo(() => {
    if (isSearchable) return dataArray;
    return dataArray.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [dataArray, searchTerm, isSearchable]);

  const handleInputChange = useCallback(
    (value) => {
      setSearchTerm(value);
      setHighlightedIndex(-1);
      if (!isOpen) setIsOpen(true);
      if (onInputChange) {
        onInputChange(value);
      }
    },
    [isOpen, onInputChange]
  );

  const handleSelect = (option, fieldOnChange, currentValue) => {
    let newValue;
    const fullOption = { ...option };

    if (isMulti) {
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      const alreadySelected = currentArray.some(
        (opt) => opt.value === option.value
      );
      newValue = alreadySelected
        ? currentArray.filter((opt) => opt.value !== option.value)
        : [...currentArray, fullOption];
    } else {
      newValue = fullOption;
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
    }

    if (fieldOnChange) fieldOnChange(newValue);
    if (onChange) onChange(newValue, fullOption);
  };

  const handleRemoveChip = (option, fieldOnChange, currentValue, e) => {
    e.stopPropagation();
    if (!isMulti) return;

    const currentArray = Array.isArray(currentValue) ? currentValue : [];
    const newValue = currentArray.filter((opt) => opt.value !== option.value);

    if (fieldOnChange) fieldOnChange(newValue);
    if (onChange) onChange(newValue, option);
  };

  const handleClear = (fieldOnChange) => {
    const newValue = isMulti ? [] : null;
    setSearchTerm("");
    setHighlightedIndex(-1);

    if (fieldOnChange) fieldOnChange(newValue);
    if (onChange) onChange(newValue);
  };

  const handleSelectAll = (fieldOnChange, currentValue) => {
    if (!isMulti) return;

    const currentArray = Array.isArray(currentValue) ? currentValue : [];
    const allCurrentlySelected = filteredOptions.every((option) =>
      currentArray.some((opt) => opt.value === option.value)
    );

    const newValue = allCurrentlySelected
      ? currentArray.filter(
        (opt) => !filteredOptions.some((option) => option.value === opt.value)
      )
      : [...new Set([...currentArray, ...filteredOptions])];

    if (fieldOnChange) fieldOnChange(newValue);
    if (onChange) onChange(newValue);
  };

  // const handleKeyDown = useCallback(
  //   (e, fieldOnChange, currentValue) => {
  //     if (!isOpen) {
  //       if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
  //         e.preventDefault();
  //         setIsOpen(true);
  //       }
  //       return;
  //     }

  //     switch (e.key) {
  //       case "ArrowDown":
  //         e.preventDefault();
  //         setHighlightedIndex((prev) =>
  //           prev < filteredOptions.length - 1 ? prev + 1 : 0
  //         );
  //         break;
  //       case "ArrowUp":
  //         e.preventDefault();
  //         setHighlightedIndex((prev) =>
  //           prev > 0 ? prev - 1 : filteredOptions.length - 1
  //         );
  //         break;
  //       case "Enter":
  //         e.preventDefault();
  //         if (
  //           highlightedIndex >= 0 &&
  //           highlightedIndex < filteredOptions.length
  //         ) {
  //           handleSelect(
  //             filteredOptions[highlightedIndex],
  //             fieldOnChange,
  //             currentValue
  //           );
  //         }
  //         break;
  //       case "Escape":
  //         e.preventDefault();
  //         setIsOpen(false);
  //         setSearchTerm("");
  //         setHighlightedIndex(-1);
  //         break;
  //       default:
  //         break;
  //     }
  //   },
  //   [isOpen, filteredOptions, highlightedIndex]
  // );

  const handleKeyDown = useCallback(
    (e, fieldOnChange, currentValue) => {

      // STOP event from reaching parent handlers
      e.stopPropagation();

      if (!isOpen) {
        if (e.key === "Tab" && onTabThenEnterWhenClosed) {
          e.preventDefault();
          tabPressedWhenClosedRef.current = true;
          return;
        }
        if (e.key === "Enter") {
          if (tabPressedWhenClosedRef.current && onTabThenEnterWhenClosed) {
            tabPressedWhenClosedRef.current = false;
            e.preventDefault();
            onTabThenEnterWhenClosed();
            return;
          }
          tabPressedWhenClosedRef.current = false;
          if (e.ctrlKey && onCtrlEnterWhenClosed) {
            e.preventDefault();
            onCtrlEnterWhenClosed();
            return;
          }
          if (onEnterWhenClosed) {
            e.preventDefault();
            onEnterWhenClosed();
            return;
          }
          e.preventDefault();
          setIsOpen(true);
          return;
        }
        tabPressedWhenClosedRef.current = false;
        if (e.key === " " || e.key === "ArrowDown") {
          e.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
          break;

        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
          break;

        case "Enter":
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length
          ) {
            handleSelect(
              filteredOptions[highlightedIndex],
              fieldOnChange,
              currentValue
            );
          } else if (onCreateOption && searchTerm.trim()) {
            onCreateOption(searchTerm.trim());
            setSearchTerm("");
            setIsOpen(false);
            setHighlightedIndex(-1);
          }
          break;

        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSearchTerm("");
          setHighlightedIndex(-1);
          break;

        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isOpen,
      filteredOptions,
      highlightedIndex,
      searchTerm,
      onEnterWhenClosed,
      onCtrlEnterWhenClosed,
      onTabThenEnterWhenClosed,
      onCreateOption,
    ]
  );

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement =
        listRef.current.children[highlightedIndex + (isMulti ? 1 : 0)];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, isMulti]);

  const getDropdownIcon = () => {
    if (isLoading || isSearching) {
      return (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 flex-shrink-0"></div>
      );
    }
    if (isSearchable) {
      return (
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
      );
    }
    return isOpen ? (
      <ChevronUpIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
    );
  };

  return (
    <div className="relative w-full" {...props}>
      <Controller
        name={name}
        control={control}
        defaultValue={isMulti ? [] : null}
        render={({
          field: { onChange: fieldOnChange, value, ref },
          fieldState,
        }) => {
          // Use the field value directly from react-hook-form
          const displayValue = normalizeValue(
            controlledValue !== undefined ? controlledValue : value
          );

          const actualHasValue = isMulti
            ? Array.isArray(displayValue) && displayValue.length > 0
            : displayValue !== null &&
            displayValue !== undefined &&
            displayValue?.value !== undefined;

          const hasError = Boolean(
            (error || fieldState.error) && !actualHasValue
          );

          // When placeholder exists, keep label floated so it doesn't overlap placeholder
          const shouldFloatLabel =
            isFloatingLabel &&
            (isOpen || actualHasValue || Boolean(searchTerm) || Boolean(placeholder?.trim()));

          // Calculate allSelected based on current field value
          const currentAllSelected =
            isMulti &&
            filteredOptions.length > 0 &&
            filteredOptions.every((option) =>
              Array.isArray(displayValue)
                ? displayValue.some((opt) => opt.value === option.value)
                : false
            );

          return (
            <>
              {label && (
                <label
                  htmlFor={name}
                  className={`${isFloatingLabel
                      ? `absolute transition-all duration-200 ease-in-out pointer-events-none z-10 ${shouldFloatLabel
                        ? "top-0 text-xs scale-75 origin-top-left bg-white px-1"
                        : "top-1/2 text-sm scale-100 origin-center"
                      }`
                      : "block mb-1"
                    } ${fontSize} ${hasError
                      ? "text-red-500"
                      : shouldFloatLabel
                        ? "text-blue-600"
                        : "text-gray-800"
                    }`}
                  style={
                    isFloatingLabel
                      ? {
                        left: hasLeftIcon
                          ? shouldFloatLabel
                            ? "52px"
                            : "52px"
                          : shouldFloatLabel
                            ? "14px"
                            : "14px",
                        transform: shouldFloatLabel
                          ? "translateY(-50%) scale(0.75)"
                          : "translateY(-50%) scale(1)",
                        transformOrigin: "left center",
                      }
                      : {}
                  }
                >
                  {label}
                </label>
              )}

              <div className="relative" ref={wrapperRef}>
                <div
                  className={`relative flex items-stretch transition-all ${hasError
                      ? "focus-within:ring-1 focus-within:ring-red-500 focus-within:ring-offset-0"
                      : "focus-within:ring-1 focus-within:ring-blue-500 focus-within:ring-offset-0"
                    } rounded-md`}
                >
                  {hasLeftIcon && (
                    <div
                      className={`flex items-center justify-center w-10 ${getHeightClass()} ${iconBgColor} border border-r-0 ${hasError ? "border-red-500" : "border-gray-300"
                        } rounded-l-md ${onLeftIconClick ? "cursor-pointer hover:opacity-80" : ""
                        } ${isDisabled ? "opacity-50" : ""
                        } transition-colors flex-shrink-0`}
                      style={{ minWidth: "2.5rem", width: "2.5rem" }}
                      onClick={onLeftIconClick}
                    >
                      {React.cloneElement(leftIcon, {
                        className: `w-4 h-4 flex-shrink-0 ${leftIcon.props?.className || "text-gray-500"
                          }`,
                      })}
                    </div>
                  )}

                  <div
                    className={`flex items-center flex-1 min-w-0 ${selectSize} ${fontSize} border ${inputBgColor} ${hasError ? "border-red-500" : "border-gray-300"
                      } ${isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                      } ${hasLeftIcon ? "rounded-l-none border-l-0" : "rounded-l-md"
                      } ${hasRightIcon
                        ? "rounded-r-none border-r-0"
                        : "rounded-r-md"
                      } focus:outline-none focus:ring-0 focus:border-transparent transition-colors ${isMulti && displayValue?.length > 0
                        ? "items-start py-1"
                        : "items-center"
                      }`}
                    onClick={() => {
                      if (isDisabled) return;
                      if (suppressNextHeaderToggleRef.current) {
                        suppressNextHeaderToggleRef.current = false;
                        return;
                      }
                      setIsOpen((prev) => !prev);
                    }}
                    onFocus={() => {
                      if (!openOnFocus || isDisabled || isOpen) return;
                      setIsOpen(true);
                      suppressNextHeaderToggleRef.current = true;
                      // Keyboard Tab: no follow-up click — allow a later header click to close normally.
                      window.setTimeout(() => {
                        suppressNextHeaderToggleRef.current = false;
                      }, 0);
                    }}
                    onKeyDown={(e) => handleKeyDown(e, fieldOnChange, value)}
                    tabIndex={isDisabled ? -1 : 0}
                  >
                    <div className="flex-1 min-w-0 overflow-hidden">
                      {isOpen ? (
                        <input
                          ref={(el) => {
                            inputRef.current = el;
                            if (ref) ref(el);
                          }}
                          type="text"
                          value={searchTerm}
                          onChange={(e) => handleInputChange(e.target.value)}
                          placeholder={placeholder}
                          disabled={isDisabled}
                          className={`w-full outline-none bg-transparent px-3 py-1 ${fontSize}`}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) =>
                            handleKeyDown(e, fieldOnChange, value)
                          }
                          autoFocus
                        />
                      ) : isMulti ? (
                        displayValue?.length > 0 ? (
                          <div className="flex items-center gap-1 px-3 py-1 max-h-16 overflow-y-auto">
                            {displayValue
                              .slice(0, maxChipsDisplay)
                              .map((option) => (
                                <span
                                  key={option.value}
                                  className={`inline-flex items-center ${chipColor} ${chipTextColor} text-xs px-2 py-1 rounded-full border flex-shrink-0`}
                                >
                                  <span className="truncate max-w-24">
                                    {option.label}
                                  </span>
                                  {!isDisabled && (
                                    <XMarkIcon
                                      className="h-3 w-3 ml-1 cursor-pointer hover:text-red-500 flex-shrink-0"
                                      onClick={(e) =>
                                        handleRemoveChip(
                                          option,
                                          fieldOnChange,
                                          value,
                                          e
                                        )
                                      }
                                    />
                                  )}
                                </span>
                              ))}
                            {displayValue.length > maxChipsDisplay && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full flex-shrink-0">
                                +{displayValue.length - maxChipsDisplay} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span
                            className={`block px-3 py-1 text-gray-400 ${fontSize}`}
                          >
                            {/* Show placeholder only when label is floated to avoid overlap */}
                            {shouldFloatLabel ? placeholder : ""}
                          </span>
                        )
                      ) : (
                        <span
                          className={`block px-3 py-1 truncate ${displayValue ? "text-gray-800" : "text-gray-400"
                            } ${fontSize}`}
                        >
                          {displayValue?.label ||
                            (shouldFloatLabel ? placeholder : "")}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pr-3 flex-shrink-0">
                      {isClearable &&
                        actualHasValue &&
                        !isDisabled &&
                        !isOpen && (
                          <XMarkIcon
                            className="h-4 w-4 text-gray-500 cursor-pointer hover:text-red-500 flex-shrink-0 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClear(fieldOnChange);
                            }}
                          />
                        )}
                      {!isDisabled && getDropdownIcon()}
                    </div>
                  </div>

                  {hasRightIcon && (
                    <div
                      className={`flex items-center justify-center w-10 ${getHeightClass()} ${iconBgColor} border border-l-0 ${hasError ? "border-red-500" : "border-gray-300"
                        } rounded-r-md ${onRightIconClick
                          ? "cursor-pointer hover:opacity-80"
                          : ""
                        } ${isDisabled ? "opacity-50" : ""
                        } transition-colors flex-shrink-0`}
                      style={{ minWidth: "2.5rem", width: "2.5rem" }}
                      onClick={onRightIconClick}
                    >
                      {React.cloneElement(rightIcon, {
                        className: `w-4 h-4 flex-shrink-0 ${rightIcon.props?.className || "text-gray-500"
                          }`,
                      })}
                    </div>
                  )}
                </div>

                {isOpen && !isDisabled && (() => {
                  const menuListClassName = `bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto ${fontSize} ${
                    menuPortal
                      ? ""
                      : `absolute w-full z-50 ${
                          dropdownPosition === "top"
                            ? "bottom-full mb-1"
                            : "mt-1"
                        }`
                  }`;

                  const menuList = (
                  <div
                    ref={listRef}
                    className={menuListClassName}
                    style={menuPortal ? portalMenuStyle ?? undefined : undefined}
                  >
                    {(isSearching || isLoading) && (
                      <div className="px-4 py-3 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        {isLoading ? "Loading..." : "Searching..."}
                      </div>
                    )}

                    {!isSearching && !isLoading && (
                      <>
                        {isMulti && filteredOptions.length > 0 && (
                          <div
                            className={`px-4 py-2 cursor-pointer hover:bg-gray-200 border-b ${currentAllSelected ? "bg-blue-100" : "bg-gray-50"
                              }`}
                            onClick={() =>
                              handleSelectAll(fieldOnChange, value)
                            }
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-4 h-4 border rounded mr-2 flex items-center justify-center flex-shrink-0 ${currentAllSelected
                                    ? "bg-blue-500 border-blue-500"
                                    : "border-gray-400"
                                  }`}
                              >
                                {currentAllSelected && (
                                  <CheckIcon className="h-3 w-3 text-white" />
                                )}
                              </div>
                              <span className="font-medium">
                                {currentAllSelected
                                  ? "Deselect all"
                                  : "Select all"}
                              </span>
                            </div>
                          </div>
                        )}

                        {filteredOptions.length > 0 ? (
                          filteredOptions.map((option, index) => {
                            const isSelected = isMulti
                              ? Array.isArray(displayValue) &&
                              displayValue.some(
                                (opt) => opt.value === option.value
                              )
                              : displayValue?.value === option.value;

                            const isHighlighted = index === highlightedIndex;

                            return (
                              <div
                                key={`${option.id || option.value}-${option.value
                                  }`}
                                className={`px-4 py-2 cursor-pointer transition-colors ${isHighlighted
                                    ? "bg-blue-100"
                                    : isSelected
                                      ? "bg-blue-500 text-white hover:bg-blue-600"
                                      : "text-gray-800 hover:bg-gray-100"
                                  }`}
                                onClick={() =>
                                  handleSelect(option, fieldOnChange, value)
                                }
                                onMouseEnter={() => setHighlightedIndex(index)}
                              >
                                {isMulti ? (
                                  <div className="flex items-center">
                                    <div
                                      className={`w-4 h-4 border rounded mr-3 flex items-center justify-center flex-shrink-0 ${isSelected
                                          ? "bg-white border-white"
                                          : "border-gray-400"
                                        }`}
                                    >
                                      {isSelected && (
                                        <CheckIcon className="h-3 w-3 text-blue-500" />
                                      )}
                                    </div>
                                    <span className="flex-1 truncate">
                                      {option.label}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="truncate">
                                    {option.label}
                                  </span>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="px-4 py-3 text-center text-gray-500">
                            {searchTerm
                              ? `No options found for "${searchTerm}"`
                              : "No options available"}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  );

                  return menuPortal && portalMenuStyle
                    ? createPortal(menuList, document.body)
                    : menuPortal
                      ? null
                      : menuList;
                })()}
              </div>
            </>
          );
        }}
      />
    </div>
  );
};

SearchDropdown.propTypes = {
  isDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isFloatingLabel: PropTypes.bool,
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  dataArray: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  placeholder: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onCreateOption: PropTypes.func,
  onInputChange: PropTypes.func,
  onSearch: PropTypes.func,
  onBlur: PropTypes.func,
  defaultValue: PropTypes.any,
  value: PropTypes.any,
  fontSize: PropTypes.string,
  selectSize: PropTypes.string,
  leftIcon: PropTypes.element,
  rightIcon: PropTypes.element,
  iconBgColor: PropTypes.string,
  inputBgColor: PropTypes.string,
  onLeftIconClick: PropTypes.func,
  onRightIconClick: PropTypes.func,
  chipColor: PropTypes.string,
  chipTextColor: PropTypes.string,
  maxChipsDisplay: PropTypes.number,
  error: PropTypes.object,
  isLoading: PropTypes.bool,
  menuPosition: PropTypes.oneOf(["top", "bottom", "auto"]),
  menuPortal: PropTypes.bool,
};

export default SearchDropdown;
